// v77.43 — Reusable "Report a bug" dialog.
//
// Mounted at multiple entry points (More menu, Help page, eventually
// per-page corners). Captures rich context automatically:
//   • current route (router location)
//   • selected child id & whether we're in "All children" mode
//   • user-agent + viewport
//   • app version (sourced from process.env.REACT_APP_VERSION when set)
// so admins can reproduce the issue without re-asking the reporter.
//
/* eslint-disable react-hooks/set-state-in-effect --
 * v77.99.75: useEffect that resets form state when the dialog opens
 * is a canonical pattern. Rule is over-eager.
 */
// Screenshot uploads go through the existing /files/upload endpoint;
// only the resulting `file_id` is forwarded so the backend stays a thin
// receiver.

import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useChildScope } from "@/context/ChildScopeContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Bug, Paperclip, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const APP_VERSION = process.env.REACT_APP_VERSION || "preview";

export function BugReportDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const { selectedChildId } = useChildScope();
  const location = useLocation();

  const [description, setDescription] = useState("");
  const [intent, setIntent] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef(null);

  // Reset on close so reopening starts blank — the "submitted" state
  // is the friendly thank-you screen and should also reset on close.
  useEffect(() => {
    if (!open) {
      setDescription("");
      setIntent("");
      setFile(null);
      setSubmitted(false);
      setSubmitting(false);
    }
  }, [open]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Screenshot must be under 5 MB");
      return;
    }
    setFile(f);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!description.trim()) {
      toast.error("Please describe what happened");
      return;
    }
    setSubmitting(true);
    try {
      let screenshot_file_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const up = await api.post("/files/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        screenshot_file_id = up?.data?.file_id || up?.data?.id || null;
      }
      await api.post("/bug-reports", {
        description: description.trim(),
        intent: intent.trim() || null,
        screenshot_file_id,
        route: location.pathname + (location.search || ""),
        selected_child_id: selectedChildId || null,
        is_all_children_view: !selectedChildId,
        user_agent: navigator.userAgent || null,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        app_version: APP_VERSION,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't submit — try again");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        preventOutsideClose
        className="bg-white dark:bg-[#1A2225] border-[#D9E1E3] dark:border-[#2A3539] max-w-lg"
        data-testid="bug-report-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Bug className="w-5 h-5 text-[#1F9FA4]" strokeWidth={1.75} />
            Report a bug
          </DialogTitle>
          {!submitted && (
            <DialogDescription className="text-[#6F828A] dark:text-[#A8B6BB]">
              Tell us what went wrong. We capture the page, your device,
              and the child you were viewing automatically — only admins
              see this report.
            </DialogDescription>
          )}
        </DialogHeader>

        {submitted ? (
          <div className="px-1 py-6 text-center" data-testid="bug-report-success">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#D6F0EF] inline-flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-[#116B6E]" strokeWidth={1.75} />
            </div>
            <h3 className="font-heading text-lg font-medium mt-4 text-[#2E3A3F] dark:text-[#E8EEF0]">
              Thanks, your report has been submitted.
            </h3>
            <p className="text-sm text-[#6F828A] dark:text-[#A8B6BB] mt-2 max-w-sm mx-auto">
              The team will review it shortly. We&apos;ll only get back to you if we
              need a quick clarification.
            </p>
            <DialogFooter className="mt-6">
              <Button
                onClick={() => onOpenChange(false)}
                className="w-full bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm"
                data-testid="bug-report-close-success"
              >
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 px-1" data-testid="bug-report-form">
            <div>
              <Label htmlFor="bug-desc">What happened? <span className="text-[#C0291D]">*</span></Label>
              <Textarea
                id="bug-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                placeholder="Describe the issue you ran into…"
                className="mt-1.5"
                data-testid="bug-report-description"
              />
            </div>
            <div>
              <Label htmlFor="bug-intent">What were you trying to do? <span className="text-[#6F828A] font-normal">(optional)</span></Label>
              <Textarea
                id="bug-intent"
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                rows={2}
                placeholder="e.g. Add a school pickup event for Tuesday"
                className="mt-1.5"
                data-testid="bug-report-intent"
              />
            </div>
            <div>
              <Label>Screenshot <span className="text-[#6F828A] font-normal">(optional, up to 5 MB)</span></Label>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={onPickFile}
                  className="sr-only"
                  data-testid="bug-report-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-[#D9E1E3] dark:border-[#2A3539] rounded-sm"
                  data-testid="bug-report-pick-file"
                >
                  <Paperclip className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Attach screenshot
                </Button>
                {file && (
                  <span
                    className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-sm bg-[#EEF3F4] dark:bg-[#1F2A2D] text-[#2E3A3F] dark:text-[#A8B6BB]"
                    data-testid="bug-report-file-pill"
                  >
                    {file.name}
                    <button
                      type="button"
                      onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                      className="text-[#6F828A] hover:text-[#C0291D]"
                      aria-label="Remove file"
                      data-testid="bug-report-file-remove"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </span>
                )}
              </div>
            </div>
            <div className="text-xs text-[#6F828A] dark:text-[#7A8A91] border border-[#EEF3F4] dark:border-[#2A3539] rounded-sm px-3 py-2" data-testid="bug-report-context-hint">
              Auto-included: <span className="font-mono-data text-[#2E3A3F] dark:text-[#A8B6BB]">{location.pathname}</span>
              {" · "}
              {selectedChildId ? "scoped to one child" : "All children view"}
              {" · "}
              {user?.email}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
                className="border-[#D9E1E3] dark:border-[#2A3539] rounded-sm"
                data-testid="bug-report-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting || !description.trim()}
                className="bg-[#1F9FA4] hover:bg-[#178085] text-white rounded-sm"
                data-testid="bug-report-submit"
              >
                {submitting ? "Submitting…" : "Submit report"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default BugReportDialog;
